import { setBadgeCount } from 'app/lib/badge';
import { recordError } from 'app/lib/monitoring';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from 'app/lib/axios';
import { trackOnFacebook, CustomFacebookEvent, ContributionType } from 'app/lib/analytics';
import { asFormData } from 'app/lib/form';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { useOnMessageReceived } from './index';
import { messageKeys } from '../keys';
import { getBffQueryData, useQueryLPC } from 'app/lib/query';
import { useAtom, useAtomValue } from 'jotai/index';
import { bffUserProfileLoadedAtom, isLoggedInAtom, visibleChannelAtom } from 'app/atoms';
import { useEffect, useRef } from 'react';
import { SUPPORT_CHANNEL_URL } from './channel';
import { type MessageCreateResponse, type MessageCreateInput, CustomChannelType } from 'app/types/chat';
import { useQueryKeys } from '../invalidation';

export const MESSAGE_ID_LOADING = '-1';

export const THUMBS_UP = '👍';
export const RED_HEART = '❤️';

export function useCountUnreadMessages(opts: FunctionalUseQueryOptions<{ unread_count: number }> = {}) {
  const client = useQueryClient();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  const queryKey = messageKeys.unread();
  const query = useQueryLPC<{ unread_count: number }>({
    queryKey,
    queryFn: async () =>
      (bffUserProfileLoaded ? getBffQueryData<{ unread_count: number }>(client, queryKey) : undefined) ??
      (await http.get<{ unread_count: number }>('/chat/unread')).data,
    ...opts,
    enabled: isLoggedIn && bffUserProfileLoaded,
  });
  const unreadCount = query.data?.unread_count;
  useEffect(() => {
    if (isLoggedIn && unreadCount !== undefined)
      void setBadgeCount(unreadCount, 'useCountUnreadMessages').catch(recordError);
  }, [isLoggedIn, unreadCount]);
  return query;
}

export function useCreateOneMessage(opts: UseMutationOptions<MessageCreateResponse, Error, MessageCreateInput> = {}) {
  const qk = useQueryKeys();
  return useMutation<MessageCreateResponse, Error, MessageCreateInput>({
    mutationFn: ({ channel_url, ...rest }) =>
      http
        .post<MessageCreateResponse>(`/chat/channels/${channel_url}/messages`, asFormData(rest))
        .then((res) => res.data),
    ...opts,
    retry: false,
    onSuccess(data, variables, context) {
      void qk.channelDetail.invalidate(variables.channel_url);
      if (data.channel.type === CustomChannelType.Support) {
        void qk.channelDetail.invalidate(SUPPORT_CHANNEL_URL);
      }
      void qk.activeChannels.invalidate();
      trackOnFacebook(CustomFacebookEvent.Contributed, { contribution_type: ContributionType.SentMessage });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export interface MessagesPushWatcher {
  pushNotifHandled: (channelUrl: string) => boolean;
}

export function useMessagesPushWatcher() {
  const onMessagedReceivedCallback = useOnMessageReceived();

  const [visibleChannel] = useAtom(visibleChannelAtom);
  const visibleChannelRef = useRef(visibleChannel);
  useEffect(() => {
    visibleChannelRef.current = visibleChannel;
  }, [visibleChannel]);

  return {
    pushNotifHandled: (channelUrl: string) => {
      void onMessagedReceivedCallback(channelUrl);

      if (channelUrl && channelUrl === visibleChannelRef.current) {
        console.log(
          `User is on screen for ${visibleChannelRef.current}; skipping push notification, and refreshing messages.`,
        );
        return true;
      }

      return false;
    },
  };
}
